import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosDelete, axiosGet, axiosPost, axiosPatch } from 'Redux/axiosHelper'
import {
  LeagueUpdateCommentType,
  LeagueUpdateCommentsResponse,
  LeagueUpdateLocation,
  LeagueUpdatePayloadType,
  LeagueUpdateRespType,
  LeagueUpdateType,
  LeagueUpdateVisibility,
  LeagueUpdatesQueryParams,
  LeagueUpdatesResponse,
  ReactionPayload,
  ReactionsResponse,
  UpdateAuthor,
} from 'types/league/leagueUpdates'

// Create a league update
const createLeagueUpdate = async (payload: LeagueUpdatePayloadType) => {
  const response = await axiosPost(payload, `v1/league-updates`)
  return response.data
}

export const useCreateLeagueUpdate = () => {
  const queryClient = useQueryClient()
  return useMutation(createLeagueUpdate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['league-updates'])
    },
  })
}

// Get all updates of a league (pinned + paginated)
const getLeagueUpdates = async ({ leagueId, ...params }: LeagueUpdatesQueryParams & { leagueId: string }) => {
  const response = await axiosGet(`v1/league-updates/by-league/${leagueId}`, params)
  return response.data
}

export const useGetLeagueUpdates = (params: LeagueUpdatesQueryParams & { leagueId: string }) => {
  return useQuery<LeagueUpdatesResponse>(
    ['league-updates', params.leagueId, params],
    () => getLeagueUpdates(params),
    { enabled: !!params.leagueId },
  )
}

// Get a single league update by id
const getLeagueUpdateById = async (updateId: string) => {
  const response = await axiosGet(`v1/league-updates/${updateId}`)
  return response.data
}

export const useGetLeagueUpdateById = (updateId: string) => {
  return useQuery<LeagueUpdateRespType>(
    ['league-update', updateId],
    () => getLeagueUpdateById(updateId),
    { enabled: !!updateId },
  )
}

// Edit a league update
const editLeagueUpdate = async ({
  updateId,
  payload,
}: {
  updateId: string
  payload: {
    title?: string
    content: string
    type?: LeagueUpdateType
    kidIds?: string[]
    imageIds?: string[]
    audioIds?: string[]
    videoIds?: string[]
    documentIds?: string[]
    sendNotification?: boolean
    visibility?: LeagueUpdateVisibility
    address?: LeagueUpdateLocation
  }
}) => {
  const response = await axiosPatch(payload, `v1/league-updates/${updateId}`)
  return response.data
}

export const useEditLeagueUpdate = () => {
  const queryClient = useQueryClient()
  return useMutation(editLeagueUpdate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['league-updates'])
      queryClient.invalidateQueries(['league-update'])
    },
  })
}

// Delete (archive) a league update
const deleteLeagueUpdate = async (updateId: string) => {
  const response = await axiosDelete(`v1/league-updates/${updateId}`)
  return response.data
}

export const useDeleteLeagueUpdate = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteLeagueUpdate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['league-updates'])
      queryClient.invalidateQueries(['league-update'])
    },
  })
}

// Pin a league update (admin only)
const pinLeagueUpdate = async (updateId: string) => {
  const response = await axiosPost({}, `v1/league-updates/pin/${updateId}`)
  return response.data
}

export const usePinLeagueUpdate = () => {
  const queryClient = useQueryClient()
  return useMutation(pinLeagueUpdate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['league-updates'])
    },
  })
}

// Unpin a league update (admin only)
const unpinLeagueUpdate = async (updateId: string) => {
  const response = await axiosDelete(`v1/league-updates/unpin/${updateId}`)
  return response.data
}

export const useUnpinLeagueUpdate = () => {
  const queryClient = useQueryClient()
  return useMutation(unpinLeagueUpdate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['league-updates'])
    },
  })
}

// Add / update / remove reaction on a league update
const addReaction = async ({
  updateId,
  payload,
}: {
  updateId: string
  payload: ReactionPayload
}) => {
  const response = await axiosPost(payload, `v1/league-updates/reaction/${updateId}`)
  return response.data
}

export const useAddReaction = () => {
  const queryClient = useQueryClient()
  return useMutation(addReaction, {
    onSuccess: () => {
      queryClient.invalidateQueries(['league-updates'])
      queryClient.invalidateQueries(['league-update'])
      queryClient.invalidateQueries(['reactions'])
    },
  })
}

// Delete a reaction by reactionId
const deleteReaction = async (reactionId: string) => {
  const response = await axiosDelete(`v1/league-updates/reaction/${reactionId}`)
  return response.data
}

export const useDeleteReaction = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteReaction, {
    onSuccess: () => {
      queryClient.invalidateQueries(['league-updates'])
      queryClient.invalidateQueries(['league-update'])
      queryClient.invalidateQueries(['reactions'])
    },
  })
}

// Get distinct authors for the filter dropdown
const getUpdateAuthors = async (leagueId: string) => {
  const response = await axiosGet(`v1/league-updates/authors/${leagueId}`)
  return response.data as UpdateAuthor[]
}

export const useGetUpdateAuthors = (leagueId: string) => {
  return useQuery<UpdateAuthor[]>(
    ['league-update-authors', leagueId],
    () => getUpdateAuthors(leagueId),
    { enabled: !!leagueId },
  )
}

// Get all reactions of a league update
const getReactions = async (updateId: string) => {
  const response = await axiosGet(`v1/league-updates/reactions/${updateId}`)
  return response.data
}

export const useGetReactions = (updateId: string) => {
  return useQuery<ReactionsResponse>(
    ['reactions', updateId],
    () => getReactions(updateId),
    { enabled: !!updateId },
  )
}

// ── Comments ──────────────────────────────────────────────────────────────────

const getLeagueUpdateComments = async ({
  updateId,
  page,
  limit,
}: {
  updateId: string
  page: number
  limit?: number
}) => {
  const response = await axiosGet(`v1/league-updates/comments/${updateId}`, { page, limit: limit ?? 20 })
  return response.data
}

export const useGetLeagueUpdateComments = (updateId: string, page = 1, enabled = true) => {
  return useQuery<LeagueUpdateCommentsResponse>(
    ['league-update-comments', updateId, page],
    () => getLeagueUpdateComments({ updateId, page }),
    { enabled: !!updateId && enabled },
  )
}

const addLeagueUpdateComment = async ({
  updateId,
  payload,
}: {
  updateId: string
  payload: { content: string; replyToCommentId?: string }
}) => {
  const response = await axiosPost(payload, `v1/league-updates/comment/${updateId}`)
  return response.data
}

export const useAddLeagueUpdateComment = () => {
  const queryClient = useQueryClient()
  return useMutation(addLeagueUpdateComment, {
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries(['league-update-comments', vars.updateId])
      queryClient.invalidateQueries(['league-updates'])
    },
  })
}

const deleteLeagueUpdateComment = async (commentId: string) => {
  const response = await axiosDelete(`v1/league-updates/comment/${commentId}`)
  return response.data
}

export const useDeleteLeagueUpdateComment = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteLeagueUpdateComment, {
    onSuccess: () => {
      queryClient.invalidateQueries(['league-update-comments'])
      queryClient.invalidateQueries(['league-updates'])
    },
  })
}
